<template>
  <section class="admin-pitches-view">

    <!-- Success/Error Modals -->
    <div v-if="showSuccessModal" class="acknowledgment-modal success-modal">
      <div class="modal-content">
        <i class="fa-solid fa-circle-check"></i>
        <p>{{ successMessage }}</p>
      </div>
    </div>

    <div v-if="showErrorModal" class="acknowledgment-modal error-modal">
      <div class="modal-content">
        <i class="fa-solid fa-circle-exclamation"></i>
        <p>{{ errorMessage }}</p>
      </div>
    </div>

    <!-- Header Section -->
    <div class="admin-header">
      <div class="header-content">
        <h1>Pitch Management</h1>
        <p>Manage and view all sports pitches in the system</p>
      </div>
      <div class="header-stats">
        <div class="stat-card">
          <i class="fa-solid fa-futbol"></i>
          <div class="stat-info">
            <h3>{{ totalPitches }}</h3>
            <p>Total Pitches</p>
          </div>
        </div>
        <div class="stat-card">
          <i class="fa-solid fa-check-circle"></i>
          <div class="stat-info">
            <h3>{{ activePitches }}</h3>
            <p>Active</p>
          </div>
        </div>
        <div class="stat-card">
          <i class="fa-solid fa-pause-circle"></i>
          <div class="stat-info">
            <h3>{{ pendingPitches }}</h3>
            <p>Pending</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="filters-container">
        <div class="search-box">
          <i class="fa-solid fa-search"></i>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search pitches by name, owner, or location..." 
            @input="handleSearch"
          />
        </div>
        
        <div class="filter-controls">
          <select v-model="statusFilter" @change="filterPitches" class="filter-select">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>

          <select v-model="typeFilter" @change="filterPitches" class="filter-select">
            <option value="">All Types</option>
            <option value="football">Football</option>
            <option value="basketball">Basketball</option>
            <option value="tennis">Tennis</option>
            <option value="rugby">Rugby</option>
            <option value="multi-purpose">Multi-purpose</option>
          </select>

          <select v-model="tierFilter" @change="filterPitches" class="filter-select">
            <option value="">All Tiers</option>
            <option value="premium">Premium</option>
            <option value="standard">Standard</option>
            <option value="basic">Basic</option>
          </select>

          <button @click="clearFilters" class="clear-filters-btn">
            <i class="fa-solid fa-rotate-left"></i>
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Pitches Table -->
    <div class="pitches-container">
      <div class="table-card">
        <div class="table-header">
          <h2>All Pitches</h2>
          <div class="table-actions">
            <button @click="refreshPitches" class="refresh-btn" :disabled="isLoading">
              <i class="fa-solid fa-rotate" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </button>
            <button @click="exportToCSV" class="export-btn">
              <i class="fa-solid fa-download"></i>
              Export CSV
            </button>
          </div>
        </div>

        <div class="table-wrapper">
          <table class="pitches-table">
            <thead>
              <tr>
                <th @click="sortPitches('pitch_name')" class="sortable">
                  Pitch Name
                  <i class="fa-solid fa-sort"></i>
                </th>
                <th @click="sortPitches('owner_name')" class="sortable">
                  Owner
                  <i class="fa-solid fa-sort"></i>
                </th>
                <th>Type</th>
                <th>Tier</th>
                <th @click="sortPitches('hourly_rate')" class="sortable">
                  Rate
                  <i class="fa-solid fa-sort"></i>
                </th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoading">
                <td colspan="8" class="loading-row">
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  Loading pitches...
                </td>
              </tr>
              <tr v-else-if="filteredPitches.length === 0">
                <td colspan="8" class="no-data">
                  <i class="fa-solid fa-inbox"></i>
                  No pitches found
                </td>
              </tr>
              <tr 
                v-else 
                v-for="pitch in paginatedPitches" 
                :key="pitch.id"
                class="pitch-row"
                @click="viewPitchDetails(pitch)"
              >
                <td class="pitch-name">
                  <div class="name-avatar">
                    <div class="avatar">
                      {{ getInitials(pitch.pitch_name) }}
                    </div>
                    <div class="name-info">
                      <strong>{{ pitch.pitch_name }}</strong>
                      <small>Created: {{ formatDate(pitch.created_at) }}</small>
                    </div>
                  </div>
                </td>
                <td class="owner-info">
                  <strong>{{ pitch.owner_name }}</strong>
                  <small>{{ pitch.owner_email }}</small>
                </td>
                <td>
                  <span class="type-badge" :class="pitch.pitch_type">
                    <i :class="getPitchTypeIcon(pitch.pitch_type)"></i>
                    {{ formatPitchType(pitch.pitch_type) }}
                  </span>
                </td>
                <td>
                  <span class="tier-badge" :class="pitch.pitch_tier">
                    {{ formatTier(pitch.pitch_tier) }}
                  </span>
                </td>
                <td class="rate-cell">
                  <strong>Ksh {{ formatNumber(pitch.hourly_rate) }}</strong>
                  <small>/hour</small>
                </td>
                <td class="location-cell">
                  <div class="location-info">
                    <i class="fa-solid fa-location-dot"></i>
                    <div>
                      <div>{{ pitch.city }}, {{ pitch.country }}</div>
                      <small class="address-truncate">{{ pitch.address }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="status-badge" :class="pitch.status">
                    {{ formatStatus(pitch.status) }}
                  </span>
                </td>
                <td class="actions-cell">
                  <div class="action-buttons">
                    <button 
                      @click.stop="viewPitchDetails(pitch)" 
                      class="action-btn view-btn"
                      title="View Details"
                    >
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button 
                      @click.stop="togglePitchStatus(pitch)" 
                      class="action-btn status-btn"
                      :title="pitch.status === 'active' ? 'Suspend' : 'Activate'"
                    >
                      <i :class="pitch.status === 'active' ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
                    </button>
                    <button 
                      @click.stop="editPitch(pitch)" 
                      class="action-btn edit-btn"
                      title="Edit Pitch"
                    >
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="filteredPitches.length > 0" class="pagination">
          <div class="pagination-info">
            Showing {{ startIndex }}-{{ endIndex }} of {{ filteredPitches.length }} pitches
          </div>
          <div class="pagination-controls">
            <button 
              @click="previousPage" 
              :disabled="currentPage === 1"
              class="pagination-btn"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            
            <span class="page-numbers">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pitch Details Modal -->
    <div v-if="selectedPitch" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Pitch Details</h2>
          <button @click="closeModal" class="close-btn">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="details-grid">
            <!-- Basic Information -->
            <div class="details-section">
              <h3>Basic Information</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>Pitch Name:</label>
                  <span>{{ selectedPitch.pitch_name }}</span>
                </div>
                <div class="info-item">
                  <label>Type:</label>
                  <span>{{ formatPitchType(selectedPitch.pitch_type) }}</span>
                </div>
                <div class="info-item">
                  <label>Tier:</label>
                  <span class="tier-badge" :class="selectedPitch.pitch_tier">
                    {{ formatTier(selectedPitch.pitch_tier) }}
                  </span>
                </div>
                <div class="info-item">
                  <label>Hourly Rate:</label>
                  <span>Ksh {{ formatNumber(selectedPitch.hourly_rate) }}</span>
                </div>
                <div class="info-item">
                  <label>Capacity:</label>
                  <span>{{ selectedPitch.capacity || 'Not specified' }}</span>
                </div>
                <div class="info-item">
                  <label>Status:</label>
                  <span class="status-badge" :class="selectedPitch.status">
                    {{ formatStatus(selectedPitch.status) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Owner Information -->
            <div class="details-section">
              <h3>Owner Information</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>Name:</label>
                  <span>{{ selectedPitch.owner_name }}</span>
                </div>
                <div class="info-item">
                  <label>Email:</label>
                  <span>{{ selectedPitch.owner_email }}</span>
                </div>
                <div class="info-item">
                  <label>Phone:</label>
                  <span>{{ selectedPitch.owner_phone || 'Not provided' }}</span>
                </div>
              </div>
            </div>

            <!-- Location Information -->
            <div class="details-section">
              <h3>Location</h3>
              <div class="info-grid">
                <div class="info-item full-width">
                  <label>Address:</label>
                  <span>{{ selectedPitch.address }}</span>
                </div>
                <div class="info-item">
                  <label>City:</label>
                  <span>{{ selectedPitch.city }}</span>
                </div>
                <div class="info-item">
                  <label>Country:</label>
                  <span>{{ selectedPitch.country }}</span>
                </div>
              </div>
            </div>

            <!-- Amenities -->
            <div v-if="selectedPitch.amenities && selectedPitch.amenities.length > 0" class="details-section">
              <h3>Amenities</h3>
              <div class="amenities-list">
                <span 
                  v-for="amenity in selectedPitch.amenities" 
                  :key="amenity"
                  class="amenity-tag"
                >
                  <i :class="getAmenityIcon(amenity)"></i>
                  {{ formatAmenity(amenity) }}
                </span>
              </div>
            </div>

            <!-- Additional Details -->
            <div class="details-section">
              <h3>Additional Information</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>Created:</label>
                  <span>{{ formatDate(selectedPitch.created_at) }}</span>
                </div>
                <div class="info-item">
                  <label>Last Updated:</label>
                  <span>{{ formatDate(selectedPitch.updated_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn-secondary">Close</button>
          <button @click="editPitch(selectedPitch)" class="btn-primary">
            <i class="fa-solid fa-edit"></i>
            Edit Pitch
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { buildApiUrl } from "@/config/api";

export default {
  name: "AdminPitchesView",
  data() {
    return {
      isLoading: false,
      pitches: [],
      filteredPitches: [],
      selectedPitch: null,
      searchQuery: "",
      statusFilter: "",
      typeFilter: "",
      tierFilter: "",
      sortField: "created_at",
      sortDirection: "desc",
      currentPage: 1,
      itemsPerPage: 10,
      showSuccessModal: false,
      showErrorModal: false,
      successMessage: "",
      errorMessage: ""
    };
  },
  computed: {
    totalPitches() {
      return this.pitches.length;
    },
    activePitches() {
      return this.pitches.filter(p => p.status === 'active').length;
    },
    pendingPitches() {
      return this.pitches.filter(p => p.status === 'pending').length;
    },
    paginatedPitches() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPitches.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredPitches.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endIndex() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.filteredPitches.length ? this.filteredPitches.length : end;
    }
  },
  mounted() {
    this.fetchPitches();
  },
  methods: {
    async fetchPitches() {
      this.isLoading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(buildApiUrl("/api/facilities/all"), {
          method: 'GET',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch pitches');
        }
        const result = await response.json();
        const list = result.facilities || result.pitches || [];
        this.pitches = Array.isArray(list) ? list : [];
        this.filteredPitches = [...this.pitches];
        this.sortPitches('created_at');
      } catch (error) {
        console.error('Error fetching pitches:', error);
        this.showErrorMessage('Failed to load pitches: ' + error.message);
      } finally {
        this.isLoading = false;
      }
    },

    handleSearch() {
      this.currentPage = 1;
      this.filterPitches();
    },

    filterPitches() {
      let filtered = this.pitches;

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(pitch => 
          pitch.pitch_name.toLowerCase().includes(query) ||
          pitch.owner_name.toLowerCase().includes(query) ||
          pitch.city.toLowerCase().includes(query) ||
          pitch.address.toLowerCase().includes(query)
        );
      }

      // Status filter
      if (this.statusFilter) {
        filtered = filtered.filter(pitch => pitch.status === this.statusFilter);
      }

      // Type filter
      if (this.typeFilter) {
        filtered = filtered.filter(pitch => pitch.pitch_type === this.typeFilter);
      }

      // Tier filter
      if (this.tierFilter) {
        filtered = filtered.filter(pitch => pitch.pitch_tier === this.tierFilter);
      }

      this.filteredPitches = filtered;
      this.sortPitches(this.sortField);
    },

    sortPitches(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }

      this.filteredPitches.sort((a, b) => {
        let aValue = a[field];
        let bValue = b[field];

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },

    clearFilters() {
      this.searchQuery = "";
      this.statusFilter = "";
      this.typeFilter = "";
      this.tierFilter = "";
      this.currentPage = 1;
      this.filteredPitches = [...this.pitches];
    },

    viewPitchDetails(pitch) {
      this.selectedPitch = pitch;
    },

    closeModal() {
      this.selectedPitch = null;
    },

    async togglePitchStatus(pitch) {
      try {
        const token = localStorage.getItem('access_token');
        const newStatus = pitch.status === 'active' ? 'suspended' : 'active';
        
        const response = await fetch(buildApiUrl(`/api/pitch-owners/pitches/${pitch.id}/status`), {
          method: 'PATCH',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        });

        if (!response.ok) {
          throw new Error('Failed to update pitch status');
        }

        // Update local data
        pitch.status = newStatus;
        this.showSuccessMessage(`Pitch ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      } catch (error) {
        console.error('Error updating pitch status:', error);
        this.showErrorMessage('Failed to update pitch status: ' + error.message);
      }
    },

    editPitch(pitch) {
      // Navigate to edit page or open edit modal
      this.$router.push(`/admin/pitches/edit/${pitch.id}`);
    },

    refreshPitches() {
      this.fetchPitches();
    },

    exportToCSV() {
      // Simple CSV export implementation
      const headers = ['Pitch Name', 'Owner', 'Type', 'Tier', 'Hourly Rate', 'City', 'Country', 'Status'];
      const csvData = this.filteredPitches.map(pitch => [
        pitch.pitch_name,
        pitch.owner_name,
        pitch.pitch_type,
        pitch.pitch_tier,
        pitch.hourly_rate,
        pitch.city,
        pitch.country,
        pitch.status
      ]);

      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pitches-export-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    },

    // Utility methods
    getInitials(name) {
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2);
    },

    formatPitchType(type) {
      return type.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    formatTier(tier) {
      return tier.charAt(0).toUpperCase() + tier.slice(1);
    },

    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    formatNumber(number) {
      return new Intl.NumberFormat().format(number);
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getPitchTypeIcon(type) {
      const icons = {
        football: 'fa-solid fa-futbol',
        basketball: 'fa-solid fa-basketball',
        tennis: 'fa-solid fa-table-tennis-paddle-ball',
        rugby: 'fa-solid fa-football',
        'multi-purpose': 'fa-solid fa-layer-group'
      };
      return icons[type] || 'fa-solid fa-map';
    },

    getAmenityIcon(amenity) {
      const icons = {
        lighting: 'fa-solid fa-lightbulb',
        changing: 'fa-solid fa-person-booth',
        parking: 'fa-solid fa-car',
        showers: 'fa-solid fa-shower',
        equipment: 'fa-solid fa-basketball',
        cafe: 'fa-solid fa-utensils',
        wifi: 'fa-solid fa-wifi',
        first_aid: 'fa-solid fa-kit-medical'
      };
      return icons[amenity] || 'fa-solid fa-check';
    },

    formatAmenity(amenity) {
      return amenity.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    // Pagination methods
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    // Notification methods
    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccessModal = true;
      setTimeout(() => {
        this.showSuccessModal = false;
      }, 3000);
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
      setTimeout(() => {
        this.showErrorModal = false;
      }, 4000);
    }
  }
};
</script>

<style scoped>
.admin-pitches-view {
  min-height: 100vh;
  background: var(--background-color1);
  font-family: "Poppins", sans-serif;
}

/* Header Section */
.admin-header {
  background: linear-gradient(135deg, var(--border-color-hover) 0%, #2c7744 100%);
  color: white;
  padding: 120px 20px 40px;
}

.header-content {
  text-align: center;
  margin-bottom: 40px;
}

.header-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.header-content p {
  font-size: 1.1rem;
  opacity: 0.9;
}

.header-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  backdrop-filter: blur(10px);
}

.stat-card i {
  font-size: 2rem;
  opacity: 0.9;
}

.stat-info h3 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
}

.stat-info p {
  margin: 0;
  opacity: 0.8;
  font-size: 0.9rem;
}

/* Filters Section */
.filters-section {
  background: var(--background-color2);
  padding: 30px 20px;
  margin-bottom: 30px;
}

.filters-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.search-box {
  position: relative;
  max-width: 500px;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 12px 15px 12px 45px;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  font-size: 0.95rem;
  background: var(--background-color1);
  transition: all 0.3s ease;
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.filter-controls {
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-select {
  padding: 10px 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.clear-filters-btn {
  padding: 10px 15px;
  background: transparent;
  color: var(--text-color2);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-filters-btn:hover {
  border-color: var(--text-color2);
}

/* Table Container */
.pitches-container {
  max-width: 1200px;
  margin: 0 auto 40px;
  padding: 0 20px;
}

.table-card {
  background: var(--background-color2);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px;
  border-bottom: 1px solid var(--border-color);
}

.table-header h2 {
  margin: 0;
  color: var(--text-color1);
  font-size: 1.5rem;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.refresh-btn, .export-btn {
  padding: 10px 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.refresh-btn:hover:not(:disabled), .export-btn:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Table Styles */
.table-wrapper {
  overflow-x: auto;
}

.pitches-table {
  width: 100%;
  border-collapse: collapse;
}

.pitches-table th {
  background: var(--background-color1);
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: var(--text-color1);
  border-bottom: 2px solid var(--border-color);
  cursor: pointer;
  user-select: none;
}

.pitches-table th.sortable:hover {
  background: var(--border-color);
}

.pitches-table td {
  padding: 15px;
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.pitches-table tbody tr {
  transition: all 0.3s ease;
  cursor: pointer;
}

.pitches-table tbody tr:hover {
  background: var(--background-color1);
}

/* Loading and No Data States */
.loading-row, .no-data {
  text-align: center;
  padding: 40px !important;
  color: var(--text-color2);
}

.loading-row i, .no-data i {
  font-size: 1.5rem;
  margin-bottom: 10px;
  display: block;
}

/* Cell Styles */
.pitch-name .name-avatar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--border-color-hover), #2c7744);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
}

.name-info strong {
  display: block;
  margin-bottom: 4px;
}

.name-info small {
  color: var(--text-color2);
  font-size: 0.8rem;
}

.owner-info strong {
  display: block;
  margin-bottom: 4px;
}

.owner-info small {
  color: var(--text-color2);
  font-size: 0.8rem;
}

/* Badge Styles */
.type-badge, .tier-badge, .status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.type-badge.football {
  background: #e3f2fd;
  color: #1976d2;
}

.type-badge.basketball {
  background: #fce4ec;
  color: #c2185b;
}

.type-badge.tennis {
  background: #e8f5e8;
  color: #2e7d32;
}

.type-badge.rugby {
  background: #fff3e0;
  color: #ef6c00;
}

.type-badge.multi-purpose {
  background: #f3e5f5;
  color: #7b1fa2;
}

.tier-badge.premium {
  background: #fff8e1;
  color: #ff8f00;
  border: 1px solid #ff8f00;
}

.tier-badge.standard {
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #2e7d32;
}

.tier-badge.basic {
  background: #f5f5f5;
  color: #757575;
  border: 1px solid #757575;
}

.status-badge.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.status-badge.pending {
  background: #fff3e0;
  color: #ef6c00;
}

.status-badge.suspended {
  background: #ffebee;
  color: #c62828;
}

/* Rate and Location Cells */
.rate-cell strong {
  color: var(--text-color1);
}

.rate-cell small {
  color: var(--text-color2);
  font-size: 0.8rem;
}

.location-cell .location-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.location-info i {
  color: var(--border-color-hover);
  margin-top: 2px;
}

.address-truncate {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 200px;
}

/* Action Buttons */
.actions-cell {
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 5px;
  justify-content: center;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
}

.view-btn {
  background: #e3f2fd;
  color: #1976d2;
}

.view-btn:hover {
  background: #1976d2;
  color: white;
}

.status-btn {
  background: #fff3e0;
  color: #ef6c00;
}

.status-btn:hover {
  background: #ef6c00;
  color: white;
}

.edit-btn {
  background: #e8f5e8;
  color: #2e7d32;
}

.edit-btn:hover {
  background: #2e7d32;
  color: white;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-top: 1px solid var(--border-color);
}

.pagination-info {
  color: var(--text-color2);
  font-size: 0.9rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.pagination-btn {
  width: 36px;
  height: 36px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  font-size: 0.9rem;
  color: var(--text-color1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--background-color2);
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h2 {
  margin: 0;
  color: var(--text-color1);
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--background-color1);
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: var(--border-color);
}

.modal-body {
  padding: 30px;
}

.details-section {
  margin-bottom: 30px;
}

.details-section h3 {
  color: var(--text-color1);
  margin-bottom: 15px;
  font-size: 1.1rem;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-weight: 600;
  color: var(--text-color2);
  font-size: 0.9rem;
}

.info-item span {
  color: var(--text-color1);
}

.amenities-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.amenity-tag {
  padding: 8px 12px;
  background: var(--background-color1);
  border-radius: 15px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border-color);
}

.modal-footer {
  padding: 20px 30px;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 15px;
  justify-content: flex-end;
}

.btn-primary, .btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: var(--border-color-hover);
  color: white;
}

.btn-primary:hover {
  background: var(--text-color2);
  transform: translateY(-2px);
}

.btn-secondary {
  background: transparent;
  color: var(--text-color2);
  border: 2px solid var(--border-color);
}

.btn-secondary:hover {
  border-color: var(--text-color2);
}

/* Acknowledgment Modals */
.acknowledgment-modal {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 1000;
  animation: slideInRight 0.3s ease-out, fadeOut 0.3s ease-in 2s forwards;
}

.acknowledgment-modal .modal-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  min-width: 280px;
  max-width: 350px;
}

.success-modal .modal-content {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border-left: 4px solid #2E7D32;
}

.error-modal .modal-content {
  background: linear-gradient(135deg, #f44336, #d32f2f);
  color: white;
  border-left: 4px solid #b71c1c;
}

.acknowledgment-modal i {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.acknowledgment-modal p {
  margin: 0;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.4;
}

/* Animations */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .admin-header {
    padding: 100px 15px 30px;
  }

  .header-content h1 {
    font-size: 2rem;
  }

  .header-stats {
    grid-template-columns: 1fr;
  }

  .filters-container {
    gap: 15px;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .table-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .table-actions {
    justify-content: center;
  }

  .pitches-table {
    font-size: 0.8rem;
  }

  .pitches-table th,
  .pitches-table td {
    padding: 10px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 5px;
  }

  .pagination {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .modal-content {
    margin: 20px;
    max-height: calc(100vh - 40px);
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .acknowledgment-modal {
    top: 80px;
    right: 10px;
    left: 10px;
  }
  
  .acknowledgment-modal .modal-content {
    min-width: auto;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .admin-header {
    padding: 80px 10px 20px;
  }

  .header-content h1 {
    font-size: 1.7rem;
  }

  .pitches-container {
    padding: 0 10px;
  }

  .table-card {
    border-radius: 15px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 15px 20px;
    flex-direction: column;
  }
}
</style>